/**
 * Plan Limits Utilities
 *
 * Manages free and pro plan limits for the application.
 * Users start on Free plan by default.
 */

// User plan types
export type UserPlan = 'free' | 'pro';

// Free plan limits
export const FREE_PLAN_LIMITS = {
  todoLists: 1,
  thinkSpaces: 1,
  notebooks: 1,
} as const;

// Pro plan has unlimited (we use a very high number)
export const PRO_PLAN_LIMITS = {
  todoLists: Infinity,
  thinkSpaces: Infinity,
  notebooks: Infinity,
} as const;

export type ResourceType = keyof typeof FREE_PLAN_LIMITS;

/**
 * Get the limit for a resource type based on plan
 */
export const getPlanLimit = (type: ResourceType, plan: UserPlan): number => {
  return plan === 'pro' ? PRO_PLAN_LIMITS[type] : FREE_PLAN_LIMITS[type];
};

/**
 * Check if user can create more of a resource type
 */
export const canCreateMore = (
  type: ResourceType,
  currentCount: number,
  plan: UserPlan
): boolean => {
  const limit = getPlanLimit(type, plan);
  return currentCount < limit;
};

/**
 * Get remaining count for a resource type
 */
export const getRemainingCount = (
  type: ResourceType,
  currentCount: number,
  plan: UserPlan
): number => {
  const limit = getPlanLimit(type, plan);
  return Math.max(0, limit - currentCount);
};

/**
 * Get usage display string (e.g., "1 of 1 used")
 */
export const getUsageDisplay = (
  type: ResourceType,
  currentCount: number,
  plan: UserPlan
): string => {
  const limit = getPlanLimit(type, plan);
  if (limit === Infinity) {
    return `${currentCount} used`;
  }
  return `${currentCount} of ${limit} used`;
};

/**
 * Check if user is at the limit for a resource type
 */
export const isAtLimit = (type: ResourceType, currentCount: number, plan: UserPlan): boolean => {
  const limit = getPlanLimit(type, plan);
  return currentCount >= limit;
};

/**
 * Human-readable resource type names
 */
export const RESOURCE_TYPE_NAMES: Record<ResourceType, string> = {
  todoLists: 'ToDo Lists',
  thinkSpaces: 'ThinkSpaces',
  notebooks: 'Notebooks',
};

/**
 * Plan display names
 */
export const PLAN_NAMES: Record<UserPlan, string> = {
  free: 'Free',
  pro: 'Pro',
};

/**
 * Plan pricing (for display)
 */
export const PLAN_PRICING: Record<
  UserPlan,
  { monthly: number; annual: number; currency: string }
> = {
  free: { monthly: 0, annual: 0, currency: 'USD' },
  pro: { monthly: 5.95, annual: 71.4, currency: 'USD' },
};
